<template>
    <div class="container-fluid">

        <!-- Search menu -->
        <div class="col-12 col-md-4 search-comp search-hide" ref="searchcomp">
            <Search @closeSearchMenu="closeSearchMenu" />
        </div>

        <div class="row content-search">

            <!-- List spinner -->
            <tinySpinner class="col-12 col-md-4" v-if="loading" />

            <!-- Search result list -->
            <div class="col-12 col-md-4 results" id="result-list">

                <div class="row col-12 col-md-4 location-and-filter-bar">

                    <!-- Search form -->
                    <div class="col-7 col-md-9 search-control location pl-0">
                        <div class="search-field row" v-b-modal.modal-location-&-dates>
                            <span class="city col-12 text-center"
                                  :class="initDate !== undefined && endDate !== undefined ? 'lh-small' : 'lh-large'"
                            >
                                {{ searchLocation.country }} | <strong>{{ searchLocation.comuna }}</strong>
                            </span>
                            <span v-if="initDate !== undefined && endDate !== undefined" class="dates col-12 text-center">
                                {{ initDate }} - {{ endDate }}
                            </span>
                        </div>
                    </div>

                    <!-- Filter button -->
                    <div class="col-3 search-control filters pl-0">
                        <button type="button" class="btn-filters" v-b-modal.modal-filtros id="selected-filters-button">
                            {{ $t('search.filter') }}
                            <span v-if="appliedFiltersCount > 0" class="badge badge-danger">{{ appliedFiltersCount }}</span>
                        </button>
                    </div>


                    <!-- Map button -->
                    <div class="d-md-none col-2 search-control map px-0">
                        <button type="button" class="btn-map" :class="{ 'active': showMapOnMobile }" @click="toggleMap">
                            <i class="far fa-map"></i>
                        </button>
                            
                    </div>
                    
                </div>

                <div :class="!showMapOnMobile ? 'd-block' : 'd-none d-md-block'" class="results-list col-12 pt-2">

                    <!-- title, ej: 60 Espacios encontrados · Santiago,Chile · 08 de Dic - 22 de Dic -->
                    <div v-if="paginator" class="col-12 results-title px-0 text-center">
                        {{ paginator.total }} {{ $t('results.found-near') }}
                        <span class="small">({{ $t('results.page') }} {{ paginator.current_page }}/{{ paginator.total_pages }})</span>
                    </div>

                    <!-- Accomodations list -->
                    <router-link v-for="accommodation in accommodations"
                                 :key="accommodation.idalojamiento"
                                 :to="accommodationLink(accommodation.idalojamiento)"
                                 @mouseenter.native="addAnimationToMarker(accommodation.idalojamiento)"
                                 @mouseleave.native="removeAnimationToMarker(accommodation.idalojamiento)"
                                 target="_self"
                    >

                        <SearchResultItem
                            :accommodation="accommodation"
                            :id="`result-${accommodation.idalojamiento}`"
                            :data-ref="`content-${accommodation.idalojamiento}`"
                            :startDate="initDate"
                            :endDate="endDate"
                        />

                    </router-link>

                    <b-pagination
                        v-if="accommodations.length > 0 && !loading && paginator"
                        :v-model="paginator.current_page"
                        :total-rows="paginator.total"
                        :per-page="paginator.per_page"
                        @change="pageChanged"
                        size="lg"
                        :hide-goto-end-buttons=true
                        align="center"
                        class="paginator"
                    >
                    </b-pagination>

                    <!-- No result message -->
                    <div class="col-12 pl-5 pr-5 mt-3" v-if="accommodations.length === 0 && !loading">
                        <div v-html="$t('results.title')" />
                        <div>
                            {{ $t('results.contact-us') }}
                            <router-link to="/contacto">{{ $t('results.here') }}</router-link>
                            {{ $t('results.we-will-help') }}
                        </div>
                        <div v-html="$t('results.no-results-message')" />
                    </div>
                </div>
                
            </div>

            <!-- Map container -->
            <div :class="showMapOnMobile ? 'd-block' : 'd-none d-md-block'" class="maps col-12 col-md-8" id="maps"></div>

        </div>
    </div>
</template>

<script>
import Vue from "vue";
import axios from "axios";
import SearchResultItem from "./SearchResultItem";
import Search from "@/components/Banner/Search.vue";
import tinySpinner from "@/components/Loader/tinySpinner";
import { mapState, mapActions } from "vuex";
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { createCustomIcon, humanizeDate } from "@/Utils/Utils";
import MarkerInfoWindowContent from "@/components/InfoWindow/MarkerInfoWindowContent";
import ClusterInfoWindowContent from "@/components/InfoWindow/ClusterInfoWindowContent";
import priceMixin from "@/mixins/priceMixin";
import { drawPolygons, removePolygons } from "@/Utils/Polygons";
import * as moment from "moment";

export default {
    name: "SearchResults",
    components: {
        SearchResultItem,
        Search,
        tinySpinner,
        MarkerInfoWindowContent,
        ClusterInfoWindowContent,
    },

    mixins: [priceMixin],

    props: [
        "query"
    ],

    data() {
        return {
            defaultZoom: 14,
            previousZoom: 14,
            map: null,
            loading: true,
            northeast: null,
            southwest: null,
            addedAccomodations: [],
            clusterer: null,
            zIndex: 0,
            markerClusterer: null,
            tempMarker: null,
            infoWindow: null,
            photos: [],
            value: 1,
            polygons: []
        };
    },
    methods: {

        ...mapActions('search', [
            'fetchSearchResults',
            'setSearchLocation',
            'setShowMapOnMobile'
        ]),

        toggleMap() {
            this.setShowMapOnMobile(!this.showMapOnMobile);
        },

        accommodationLink(accommodationId) {
            if(this.$route.query.fecha_inicio && this.$route.query.fecha_termino)
                return "/alojamiento/" + accommodationId + '/' + this.$route.query.fecha_inicio + '/' + this.$route.query.fecha_termino;
            else
                return "/alojamiento/" + accommodationId;
        },
        
        pageChanged(page) {
            // change the page in the url
            this.$router.push({ query: { ...this.$route.query, page: page } });
            // scroll top
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            });

        },

        async createMap() {

            // Create the map and add it to the container
            const map = await new google.maps.Map(document.getElementById("maps"), {
                center: this.mapCenter,
                zoom: this.defaultZoom,
                disableDefaultUI: true,
                zoomControl: true,
                zoomControlOptions: {position: google.maps.ControlPosition.RIGHT_TOP},
                maxZoom: 16
            });

            // Info window
            this.infoWindow = new google.maps.InfoWindow({
                disableAutoPan: true
            });
            

            // Agregar evento de escucha al cambio de posición del mapa
            google.maps.event.addListener(map, 'dragend', this.mapMoved);
            google.maps.event.addListener(map, 'zoom_changed', this.mapZoomed);

            return map;
        },

        async groupInClusters() {
            // Clear markers
            this.markerClusterer.clearMarkers();

            // Set clusterer markers
            await this.markerClusterer.addMarkers(this.addedAccomodations.map(accommodation => accommodation.marker));

        },

        // Singleton pattern
        getMarkerClusterer(map) {
            if (!this.markerClusterer) {

                this.markerClusterer = new MarkerClusterer(
                    { map: map },
                    { markers: [] },
                );
            }

            return this.markerClusterer;
        },

        // Función para obtener el clúster de un marcador
        getClusterOfMarker(marker) {
            var clusters = this.markerClusterer.clusters;

            for (var i = 0; i < clusters.length; i++) {
                var markers = clusters[i].markers;

                for (var j = 0; j < markers.length; j++) {
                    if (markers[j] === marker) {
                        return clusters[i];
                    }
                }
            }

            return null;
        },

        // Animate the marker when the user hover the result item
        addAnimationToMarker(accommodationId) {

            const marker = this.addedAccomodations.find(accommodation => accommodation.id === accommodationId).marker;

            const cluster = this.getClusterOfMarker(marker);

            if (cluster && cluster.markers.length > 1) {
                // If the marker is in a cluster, create a merker show it over the cluster icon

                const clusterPosition = marker.position;

                const icon = {
                    url: "data:image/png;base64,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",
                    anchor: new google.maps.Point(47, 46),
                };

                this.tempMarker = new google.maps.Marker({
                    position: {
                        lat: clusterPosition.lat(),
                        lng: clusterPosition.lng(),
                    },
                    map: this.map,
                    icon: icon,
                });

            } else {
                // Otherwise, animate the price marker
                marker.setAnimation(google.maps.Animation.BOUNCE);
                marker.setZIndex(this.zIndex++);
            }
        },

        removeAnimationToMarker(accommodationId) {
            const marker = this.addedAccomodations.find(accommodation => accommodation.id === accommodationId).marker;
            const cluster = this.getClusterOfMarker(marker);

            if (cluster && cluster.markers.length > 1) {
                this.tempMarker.setMap(null);
            } else {
                marker.setAnimation(null);
                marker.setZIndex(this.zIndex - 1);
            }
        },

        // Add the markers to the map
        addMarkers() {

            // Clear existing markers
            this.clearMarkers();

            this.accommodations.forEach(accommodation => {

                // Create the marker
                const marker = this.createMarker(accommodation);

                // Add marker listener
                this.addMarkerListener(marker, accommodation);

                // Add the accommodation to the collection
                this.addedAccomodations.push({ id: accommodation.idalojamiento, marker: marker });

            });
        },

        clearMarkers() {
            this.addedAccomodations.forEach(({ marker }) => marker.setMap(null));
            this.addedAccomodations = [];
        },


        // Calculate the price of the accommodation
        // accommodationPrice(accommodation) {
        //     const amount = this.dailyRentalModeRequested ? this.totalDaysRequested * accommodation.arriendo_diario : accommodation.arriendo;
        //     return { 'amount': amount, 'currency': accommodation.moneda };
        // },

        // This code create a marker with a proce inside. Temporally disabled
        // createMarker(accommodation) {

        //     // Create the position
        //     let elementPosition = new google.maps.LatLng(accommodation.latitud, accommodation.longitud)

        //     const price = this.accommodationPrice(accommodation);
        //     const convertedAndFormattedPrice = Vue.options.filters.convertAndFormatPrice(price);

        //     // Create the icon
        //     const icon = { url: createCustomIcon(convertedAndFormattedPrice) };

        //     // Create the marker
        //     return new google.maps.Marker({
        //         position: elementPosition,
        //         map: this.map,
        //         icon: icon,
        //         accommodationId: accommodation.idalojamiento
        //     });
        // },

        createMarker(accommodation) {
            // Crear la posición
            let elementPosition = new google.maps.LatLng(accommodation.latitud, accommodation.longitud);

            // const price = this.accommodationPrice(accommodation);
            // const convertedAndFormattedPrice = Vue.options.filters.convertAndFormatPrice(price);

            // Crear el marcador
            return new google.maps.Marker({
                position: elementPosition,
                map: this.map,
                // title: 'Ubicación predeterminada', // Título opcional para el marcador
                // label: convertedAndFormattedPrice, // Etiqueta opcional para el marcador
                accommodationId: accommodation.idalojamiento
            });
        },

        addMarkerListener(marker, accommodation) {

            // Add the click listener to the pice label
            marker.addListener("click", async (event) => {

                const mainPhoto = await this.getAccommodationMainPhoto(accommodation);

                // Render infoWindow content
                const content = this.renderMarkerInfoWindowContent(mainPhoto, accommodation);

                // Set the infoWindow content
                this.infoWindow.setContent(content.$el);

                // Open infoWindow 
                this.infoWindow.open(this.map, marker);


                // Move the map down
                
                // Get the current center of the map
                let center = marker.getPosition();

                let mapHeight = document.getElementById("maps").offsetHeight;

                // Convert the center of the map from LatLng to Point
                let centerPoint = this.map.getProjection().fromLatLngToPoint(center);

                // Calculate the new point
                let newPoint = new google.maps.Point(
                    centerPoint.x,
                    centerPoint.y - mapHeight / 2.5 / Math.pow(2, this.map.getZoom())
                );

                // Convert the new point back to LatLng
                let newCenter = this.map.getProjection().fromPointToLatLng(newPoint);

                // Set the new center of the map
                this.map.panTo(newCenter);

            });
        },

        addClusterListener(markerClusterer) {

            // Add click event listener to cluster when the maz zoom is reached
            google.maps.event.addListener(markerClusterer, 'click', (cluster) => {
                setTimeout(() => { // workarround to move the map after the listener ends, otherwise it doesn't work
                    // Check if the map zoom is at maximum
                    if (this.map.getZoom() === 16) {
                        // Get accommodations of the markers
                        const accommodationsArray = this.accommodations.filter(accommodation => {
                            return cluster.markers.some(marker => marker.accommodationId === accommodation.idalojamiento);
                        });

                        // Render infoWindow content
                        const content = this.renderClusterInfoWindowContent(accommodationsArray);

                        // Set the infoWindow content
                        this.infoWindow.setContent(content.$el);

                        // Open infoWindow
                        setTimeout(() => {
                            if (this.map.getZoom() === 16) {
                                this.infoWindow.open(this.map, cluster.marker);
                            }
                        }, 1000);
                        

                        // Get the current center of the map
                        let center = this.map.getCenter();

                        let mapHeight = document.getElementById("maps").offsetHeight;

                        // Convert the center of the map from LatLng to Point
                        let centerPoint = this.map.getProjection().fromLatLngToPoint(center);

                        // Calculate the new point
                        let newPoint = new google.maps.Point(
                            centerPoint.x,
                            centerPoint.y - mapHeight / 2.5 / Math.pow(2, this.map.getZoom())
                        );

                        // Convert the new point back to LatLng
                        let newCenter = this.map.getProjection().fromPointToLatLng(newPoint);

                        // Set the new center of the map
                        this.map.panTo(newCenter);
                    }
                }, 0);
            });
        },

        renderMarkerInfoWindowContent(mainPhoto, accommodation) {

            const MarkerInfoWindowContentComponent = Vue.extend(MarkerInfoWindowContent);
            const MarkerInfoWindowContentInstance = new MarkerInfoWindowContentComponent({
                propsData: {
                    accommodationId: accommodation.idalojamiento,
                    imageUrl: process.env.VUE_APP_S3_URL + mainPhoto.url,
                    accommodationName: JSON.parse(accommodation.nombre)[this.$i18n.locale]
                }
            });

            return MarkerInfoWindowContentInstance.$mount();
        },

        renderClusterInfoWindowContent(accommodationsArray) {

            const ClusterInfoWindowContentComponent = Vue.extend(ClusterInfoWindowContent);

            const contentInstance = new ClusterInfoWindowContentComponent({
                propsData: {
                    accommodations: accommodationsArray,
                    locale: this.$i18n.locale
                },
            });

            return contentInstance.$mount();
        },

        getAccommodationMainPhoto(accommodation) {

            return axios({
                url:
                    process.env.VUE_APP_API_URL +
                    "galleries/searchByAccommodation/" +
                    accommodation.idalojamiento,
                method: "GET",
            }).then((resp) => {
                const accommodationGallery = resp.data.photos;
                return accommodationGallery[0];
            });

        },

        mapMoved() {
            
            this.loading = true;

            // Get new boundaries
            const bounds = this.map.getBounds();
            const northeast = bounds.getNorthEast();
            const southwest = bounds.getSouthWest();
            
            // Get map center
            const center = this.map.getCenter();

            // Set the boundaries in the query params
            this.queryParams.northeast = `${northeast.lat()},${northeast.lng()}`;
            this.queryParams.southwest = `${southwest.lat()},${southwest.lng()}`;

            // Set the center in the query params
            this.queryParams.latitud = center.lat();
            this.queryParams.longitud = center.lng();
            
            // Reset the page requested in the url and the params
            this.queryParams.page = 1;
            
            // Push the new url. The watcher is responsible to fetch the new results
            this.$router.push({ query: this.queryParams });
        },

        mapZoomed() {
            /*
            This is a workarround.
            When the cluster is clicked it zoom in, and if the zoom 
            level is more than the zoomLimit it change it again to 
            the previous value, so the listener is trigger twice 
            */
            setTimeout(() => {
                if (this.previousZoom !== this.map.zoom) {
                    this.previousZoom = this.map.zoom;
                    this.mapMoved();
                }
            }, 200);
        },

        // ======== Move to another file ========
        humanDate(date) {
            if (!date || date === 'undefined' || isNaN(new Date(date))) {
                return;
            }

            // Normalize to UTC
            const normalizedDate = moment.utc(date).format('YYYY-MM-DD');
            
            const data = humanizeDate(normalizedDate);

            return `${data[0]} ${this.$t(data[1])}`;
        },


        // Open the search menu 
        // openSearchMenu() {
        //     this.$refs.searchcomp.classList.remove("search-hide");
        // },

        // Close the search menu
        closeSearchMenu() {
            this.$refs.searchcomp.classList.add("search-hide");
        },

    },

    computed: {

        ...mapState('search', [
            'searchResults',
            'searchLocation',
            'showMapOnMobile'
        ]),

        ...mapState('filter', [
            'appliedFiltersCount',
        ]),
        
        accommodations() {
            return this.searchResults.data;
        },
        
        paginator() {
            if (this.searchResults.paginate) {
                return this.searchResults.paginate;
            }
            return false;
        },

        /**
         * The Search.vue component create the parameters array and push it 
         * to the router. The SearchResults.vue component take that url and
         * parse it to a query string
         */
        queryParams() {
            const params = {};
            for (const key in this.$route.query) {
                const value = this.$route.query[key];

                // Solo incluir valores que no sean nulos o vacíos
                if (value !== null && value !== '') {
                    // Convertir northeast y southwest a cadenas de coordenadas "lat,lng"
                    // if (key === 'northeast' || key === 'southwest') {
                    //     if (typeof value === 'object' && 'lat' in value && 'lng' in value) {
                    //         params[key] = `${value.lat},${value.lng}`;
                    //     }
                    // } else {
                        params[key] = value;
                    // }
                }
            }

            // Asegurarse de que los parámetros requeridos estén presentes
             return params;
        },

        mapCenter() {
            return {
                lat: Number(this.queryParams.latitud),
                lng: Number(this.queryParams.longitud)
            };
        },

        initDate: function () { return this.humanDate(this.queryParams.fecha_inicio); },
        endDate: function () { return this.humanDate(this.queryParams.fecha_termino); },
    },
    
    // Save the scroll position before leaving
    beforeRouteLeave(to, from, next) {
        // save scronn only when go to accommodation page
        if (to.name === 'detalle-alojamiento') {
            const scroll = document.getElementById('result-list').scrollTop;
            sessionStorage.setItem('scrollPosition',scroll);
        }
        next();
    },

    async mounted() {

        // Restore the scroll position if exists
        const scroll = sessionStorage.getItem('scrollPosition');
        if (scroll) {
            document.getElementById('result-list').scrollTop = scroll;
            sessionStorage.removeItem('scrollPosition');
        }

        // Wait for the google maps to be loaded
        await window.googleMapsLoaded;
        
        // Create the map
        this.map = await this.createMap();
        
        // Create the clusters and add listeners to clusters
        this.markerClusterer = this.getMarkerClusterer(this.map);
        this.addClusterListener(this.markerClusterer);

        // Fetch results from api
        this.loading = true;
        this.fetchSearchResults(this.queryParams)
            
            .then(() => {
                this.addMarkers();
                this.groupInClusters();
            })
            .finally(() => {
                this.loading = false;
            });

        this.setSearchLocation(this.queryParams);
    },

    watch: {

        async searchLocation(newLocation, oldLocation) {
            // Check if the new location is different from the old one
            if (newLocation !== oldLocation) {
                // Update the zoom to 14
                this.map.zoom = this.defaultZoom;
            }

            // Draw the location or neighborhood on the map
            removePolygons(this.polygons);
            if(newLocation.polygon) {
                this.polygons = await drawPolygons(newLocation, this.map);
            }
            
        },

        $route(to, from) {

            // Fetch results from api
            this.loading = true;
            this.fetchSearchResults(this.queryParams)
                .then(() => {
                    this.map.setCenter(this.mapCenter);
                    this.addMarkers();
                    this.groupInClusters();
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }
};
</script>

<style lang="scss">

.page-link {
    color: $hf-sec-pine !important;
}

#infoWindow {
    // display: none;
    // position: absolute;
    background-color: #fff;
    padding: 10px;
    color: #000000;
}

.custom-label {
    background-color: #fff;
    border: 1px solid #000;
    padding: 5px;
    border-radius: 5px;
}

a {
    text-decoration: none;

    &:hover {
        text-decoration: none !important;
    }
}

.carousel-control-next,
.carousel-control-prev {
    &:hover {
        background: rgba(0, 0, 0, 0.555);
    }
}

.search-comp {
    padding: 30px 15px;
    position: fixed !important;
    z-index: 101;
    left: 0;
    box-shadow: 0px 7px 15px #0000006e;
    background: #fff;
    transition: 0.2s ease;
    margin-top: -10px;
}

.search-hide {
    transform: translateY(-130%) !important;
}

.vue-map-container {
    position: relative;
    height: 100%;
}

.maps {
    height: calc(100vh - 145px);
    background: #000;
    color: #fff;
    padding: 0px !important;
    position: fixed !important;
    right: 0;
}

.results {
    
    .row {
        margin: 0;
    }

    padding: 0px !important;
    overflow: auto;

    .results-title {
        margin-top: 90px;
        padding-bottom: 5px;
    }
    
    

    // Paginator size
    //.page-link {
    //    padding: 0.5rem 1.2rem !important;
    //    font-size: 1.25rem !important;
    //    line-height: 1.5 !important;
    //}
}

.location-and-filter-bar {
    
    position: fixed !important;
    z-index:100;
    background-color: #FFFFFF;
    padding: 20px;
    border-bottom: 1px solid #eeeeee;
    
    .location {

        height: 55px;
        padding: 0 15px;

        .search-field {
            overflow: hidden;
            padding: 3px;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: space-evenly;
            cursor: pointer;
            background-color: #f8f9fa;
            border: 1px solid #ced4da;
            border-radius: 4px;

            .lh-small {
                line-height: 1.16;
                font-size: 14px;
            }
            .lh-large {
                line-height: 1.5;
            }
        }
    }

    .filters {

        padding: 0 15px;

        height: 55px;
        display: flex;

        .btn-filters {
            flex-grow: 1;
            cursor: pointer;
            background-color: #f8f9fa;
            border: 1px solid #ced4da;
            border-radius: 4px;
        }

        .badge {
            font-size: 0.8rem;
            margin-left: 0.3rem;

        }
    }
    
    .btn-map {

        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 55px;
        
        cursor: pointer;
        background-color: #f8f9fa;
        border: 1px solid #ced4da;
        border-radius: 4px;
        
        &.active {
            background-color: #eee !important;
            border: 1px solid #bbb;
        }
    }

    .city,
    .date,
    .separator {
        text-align: center;
    }
    
    .dates {
        text-align: center;
        font-size: 11px;
        color: #8e8e8e;
    }

    .separator {
        color: rgb(200, 200, 200);
    }

}

.bg-black {
    background: #000;
    color: #fff;
    transition: 0.2s ease;
    z-index: 10;

    &:hover {
        padding: 10px;
    }
}

.popup-container {
    cursor: auto;
    height: 0;
    position: absolute;
    width: 200px;
}

@media (max-width: 767px) {
    .results {
        height: calc(100vh - 120px);
    }
}
</style>

<style lang="scss" scoped>
@media (max-width: 1380px) {
    .col-xl-7 {
        flex: 0 0 41.666667% !important;
        max-width: 41.666667% !important;
    }

    .col-xl-5 {
        flex: 0 0 58.333333% !important;
        max-width: 58.333333% !important;
    }
}

@media (max-width: 1020px) {
    .col-12 {
        flex: 0 0 100% !important;
        max-width: 100% !important;
    }
}

</style>